import { useDispatch, useSelector } from "react-redux";
import { useEffect, useCallback } from "react";

import {
  onLoadDocumentTypes,
  onLoadCountryPhones,
  onLoadGenders,
  onLoadIpss,
  onLoadRatePlans,
  onLoadEthnicities,
  onLoadSpecialties,
  onLoadSurgeries,
  onLoadDoctors,
  onLoadHeadquarters,
  onLoadProcedures,
  onFailedLoad,
  onSuccessLoad,
  isLoadingData,
} from "../store/select_form";
import { gosafeApi } from "../api";

export const useSelectFormStore = (requestedVariables = []) => {
  const dispatch = useDispatch();
  const {
    isLoading,
    documentTypes,
    countryPhones,
    genders,
    ipss,
    ratePlans,
    ethnicities,
    specialties,
    surgeries,
    doctors,
    headquarters,
    procedures,
    errorMessage,
    successData,
  } = useSelector((state) => state.selectForm);

  const startGetDocumentTypes = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/documentType`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadDocumentTypes(data));
      }
      throw new Error("No se pudieron cargar los tipos de documentTypes.");
    } catch (error) {
      error.message = `Error en startGetDocumentTypes: ${error.message}`;
      throw error;
    }
  };

  const startGetCountryPhones = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/countryPhone`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadCountryPhones(data));
      }
      throw new Error("No se pudieron cargar los countryPhones.");
    } catch (error) {
      error.message = `Error en startGetCountryPhones: ${error.message}`;
      throw error;
    }
  };

  const startGetGenders = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/gender`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadGenders(data));
      }
      throw new Error("No se pudieron cargar los genders.");
    } catch (error) {
      error.message = `Error en startGetGenders: ${error.message}`;
      throw error;
    }
  };

  const startGetIpss = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/ips`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadIpss(data));
      }
      throw new Error("No se pudieron cargar las ipss.");
    } catch (error) {
      error.message = `Error en startGetIpss: ${error.message}`;
      throw error;
    }
  };

  const startGetRatePlans = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/ratePlan`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadRatePlans(data));
      }
      throw new Error("No se pudieron cargar las ratePlans.");
    } catch (error) {
      error.message = `Error en startGetRatePlans: ${error.message}`;
      throw error;
    }
  };

  const startGetEthnicities = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/ethnicity`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadEthnicities(data));
      }
      throw new Error("No se pudieron cargar las ethnicities.");
    } catch (error) {
      error.message = `Error en startGetEthnicities: ${error.message}`;
      throw error;
    }
  };

  const startGetSpecialties = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/specialty`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadSpecialties(data));
      }
      throw new Error("No se pudieron cargar las especialidades.");
    } catch (error) {
      error.message = `Error en startGetSpecialties: ${error.message}`;
      throw error;
    }
  };

  const startGetSurgeries = async () => {
    try {
      const { data, status } = await gosafeApi.get(`surgeries`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadSurgeries(data));
      }
      throw new Error("No se pudieron cargar las cirugias.");
    } catch (error) {
      error.message = `Error en startGetSurgeries: ${error.message}`;
      throw error;
    }
  };

  const startGetDoctors = async () => {
    try {
      const { data, status } = await gosafeApi.get(`doctorList`);
      if (status === 200 && data.length > 0) {
        const newData = data.map((doctor) => ({
          id: doctor.id,
          fullName: `${doctor.document}: ${doctor.firstName} ${doctor.lastName}`,
        }));
        return dispatch(onLoadDoctors(newData));
      }
      throw new Error("No se pudieron cargar los doctores.");
    } catch (error) {
      error.message = `Error en startGetDoctors: ${error.message}`;
      throw error;
    }
  };

  const startGetHeadquarters = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/headquarter`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadHeadquarters(data));
      }
      throw new Error("No se pudieron cargar las sedes.");
    } catch (error) {
      error.message = `Error en startGetHeadquarters: ${error.message}`;
      throw error;
    }
  };

  const startGetProcedures = async () => {
    try {
      const { data, status } = await gosafeApi.get(`demographic/procedure`);
      if (status === 200 && data.length > 0) {
        return dispatch(onLoadProcedures(data));
      }
      throw new Error("No se pudieron cargar los procedimiento.");
    } catch (error) {
      error.message = `Error en startGetProcedures: ${error.message}`;
      throw error;
    }
  };

  const dataLoaders = {
    documentTypes: {
      data: documentTypes,
      loader: startGetDocumentTypes,
    },
    countryPhones: {
      data: countryPhones,
      loader: startGetCountryPhones,
    },
    genders: {
      data: genders,
      loader: startGetGenders,
    },
    ipss: {
      data: ipss,
      loader: startGetIpss,
    },
    ratePlans: {
      data: ratePlans,
      loader: startGetRatePlans,
    },
    ethnicities: {
      data: ethnicities,
      loader: startGetEthnicities,
    },
    specialties: {
      data: specialties,
      loader: startGetSpecialties,
    },
    surgeries: {
      data: surgeries,
      loader: startGetSurgeries,
    },
    doctors: {
      data: doctors,
      loader: startGetDoctors,
    },
    headquarters: {
      data: headquarters,
      loader: startGetHeadquarters,
    },
    procedures: {
      data: procedures,
      loader: startGetProcedures,
    },
  };

  useEffect(() => {
    (async () => {
      try {
        dispatch(isLoadingData());

        const loaders = requestedVariables
          .map((key) => {
            const { data, loader } = dataLoaders[key] || {};
            return Array.isArray(data) && data.length === 0 ? loader : null;
          })
          .filter(Boolean);

        if (loaders.length > 0) {
          await Promise.all(loaders.map((loader) => loader()));
          dispatch(onSuccessLoad());
        }

        return dispatch(onSuccessLoad());
      } catch (error) {
        dispatch(onFailedLoad(error.message));
      }
    })();
  }, []);

  const returnedData = requestedVariables.reduce((acc, key) => {
    if (dataLoaders[key]) {
      acc[key] = dataLoaders[key].data;
    }
    return acc;
  }, {});

  return {
    isLoading,
    errorMessage,
    ...returnedData,
  };
};
